/**
 * Gift Price Modifier Script
 * Detects auto-applied discount labels containing "gift" keyword and changes displayed price to "FREE"
 * Works with EasyGift Script Tag (EG Auto Add to Cart) app
 */

class GiftPriceModifier {
  constructor() {
    this.init();
  }

  init() {
    // Run when DOM is loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.modifyGiftPrices());
    } else {
      this.modifyGiftPrices();
    }

    // Watch for cart updates (AJAX cart refreshes)
    this.observeCartChanges();
  }

  /**
   * Main function to modify prices for gift items to show "FREE"
   */
  modifyGiftPrices() {
    // Find all cart items in both mini-cart and main cart
    // Main cart uses .cart-item, mini-cart uses li elements in .mini-cart__navigation
    const mainCartItems = document.querySelectorAll('.cart-item');
    const miniCartItems = document.querySelectorAll('.mini-cart__navigation li');
    
    [...mainCartItems, ...miniCartItems].forEach(item => {
      if (this.hasGiftDiscount(item)) {
        this.updateItemPriceToZero(item);
      }
    });
  }

  /**
   * Check if cart item has a gift-related discount
   * @param {Element} cartItem - The cart item element
   * @returns {boolean}
   */
  hasGiftDiscount(cartItem) {
    const discountElements = cartItem.querySelectorAll('.discounts__discount');
    
    for (const discount of discountElements) {
      const discountText = discount.textContent.toLowerCase();
      if (discountText.includes('gift')) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Update the displayed price to "FREE" for gift items
   * @param {Element} cartItem - The cart item element
   */
  updateItemPriceToZero(cartItem) {
    // Find all price-money elements within this cart item
    const priceElements = cartItem.querySelectorAll('price-money bdi');
    
    priceElements.forEach(priceElement => {
      // Skip if already set to FREE
      if (priceElement.textContent === 'FREE') {
        return;
      }

      // Store original price if not already stored
      if (!priceElement.dataset.originalPrice) {
        priceElement.dataset.originalPrice = priceElement.textContent;
      }

      // Set price to FREE and add red styling class
      priceElement.textContent = 'FREE';
      priceElement.classList.add('gift-free-price');
    });

    // Handle mini-cart specific price elements
    const miniCartPrices = cartItem.querySelectorAll('.price-item');
    miniCartPrices.forEach(priceElement => {
      if (priceElement.textContent === 'FREE') {
        return;
      }

      if (!priceElement.dataset.originalPrice) {
        priceElement.dataset.originalPrice = priceElement.textContent;
      }

      // Set price to FREE and add red styling class
      priceElement.textContent = 'FREE';
      priceElement.classList.add('gift-free-price');
    });

    // Remove struck-through prices for gift items - hide the regular price when both regular and sale prices show
    const struckThroughPrices = cartItem.querySelectorAll('s.price-item--regular, s.price.price-item--regular');
    struckThroughPrices.forEach(element => {
      if (!element.dataset.originalDisplay) {
        element.dataset.originalDisplay = element.style.display || '';
      }
      element.style.display = 'none';
    });

    // Also hide struck-through prices in main cart
    const mainCartStruckThrough = cartItem.querySelectorAll('s.cart-item__old-price, s.price.price-item--regular');
    mainCartStruckThrough.forEach(element => {
      if (!element.dataset.originalDisplay) {
        element.dataset.originalDisplay = element.style.display || '';
      }
      element.style.display = 'none';
    });

    // Add CSS class for potential styling (optional)
    cartItem.classList.add('has-gift-discount');
  }





  /**
   * Observe cart changes for AJAX updates
   */
  observeCartChanges() {
    // Watch for cart drawer updates
    const cartDrawer = document.querySelector('cart-drawer, #cart-drawer');
    if (cartDrawer) {
      this.observeElement(cartDrawer);
    }

    // Watch for main cart updates
    const mainCart = document.querySelector('form[action*="cart"]');
    if (mainCart) {
      this.observeElement(mainCart);
    }

    // Listen for custom cart update events
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.modifyGiftPrices(), 100);
    });

    // Listen for cart drawer open events
    document.addEventListener('cart:open', () => {
      setTimeout(() => this.modifyGiftPrices(), 100);
    });
  }

  /**
   * Observe changes to a specific element
   * @param {Element} element - Element to observe
   */
  observeElement(element) {
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' || 
            (mutation.type === 'attributes' && mutation.attributeName === 'class')) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setTimeout(() => this.modifyGiftPrices(), 50);
      }
    });

    observer.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Restore original prices (for debugging/testing)
   */
  restoreOriginalPrices() {
    const modifiedPrices = document.querySelectorAll('[data-original-price]');
    modifiedPrices.forEach(element => {
      element.textContent = element.dataset.originalPrice;
      delete element.dataset.originalPrice;
    });

    // Restore struck-through price visibility
    const hiddenStruckThrough = document.querySelectorAll('[data-original-display]');
    hiddenStruckThrough.forEach(element => {
      element.style.display = element.dataset.originalDisplay;
      delete element.dataset.originalDisplay;
    });

    // Remove gift styling classes
    const giftFreeElements = document.querySelectorAll('.gift-free-price');
    giftFreeElements.forEach(element => element.classList.remove('gift-free-price'));

    // Remove gift discount classes
    const giftItems = document.querySelectorAll('.has-gift-discount');
    giftItems.forEach(item => item.classList.remove('has-gift-discount'));
  }
}

// Initialize the gift price modifier
const giftPriceModifier = new GiftPriceModifier();

// Make it globally available for debugging
window.giftPriceModifier = giftPriceModifier; 